import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Package, MapPin, User } from "lucide-react";

const statusColor = (s) => ({
  placed: "bg-[#F2CC8F]/40 text-[#8B6F1F]",
  confirmed: "bg-[#81B29A]/30 text-[#345C43]",
  out_for_delivery: "bg-[#E07A5F]/25 text-[#7A3020]",
  delivered: "bg-[#2C3E2D] text-white",
  cancelled: "bg-red-100 text-red-700",
})[s] || "bg-[#F0EFEA] text-[#2C3E2D]";

const Account = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-12" data-testid="account-page">
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-6 flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-[#F0EFEA] flex items-center justify-center overflow-hidden">
          {user?.picture ? <img src={user.picture} alt={user.name} className="h-full w-full object-cover" /> : <User className="h-6 w-6 text-[#81B29A]" />}
        </div>
        <div>
          <div className="tick-label">Welcome</div>
          <h1 className="font-heading text-2xl text-[#2C3E2D]">{user?.name}</h1>
          <div className="text-sm text-[#6B7B6C]">{user?.email}</div>
        </div>
      </div>

      <h2 className="font-heading text-2xl text-[#2C3E2D] mt-10 mb-4">My Orders</h2>
      {orders.length === 0 ? (
        <div className="rounded-2xl border border-[#E5E5E0] bg-white p-10 text-center text-[#6B7B6C]" data-testid="orders-empty">
          <Package className="mx-auto mb-3 h-6 w-6 text-[#81B29A]" />
          You haven&apos;t placed any orders yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.order_id} className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={`order-row-${o.order_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#6B7B6C]">Order</div>
                  <div className="font-heading text-lg text-[#2C3E2D]">{o.order_id}</div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusColor(o.order_status)}`}>{o.order_status.replace("_", " ")}</span>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-[#6B7B6C]">
                <div><span className="text-[#2C3E2D] font-medium">₹{o.total.toFixed(2)}</span> · {o.items.length} items</div>
                <div className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.address.city}, {o.address.state}</div>
                <div>{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {o.items.slice(0, 5).map((i) => (
                  <div key={i.product_id} className="flex items-center gap-2 rounded-full bg-[#F0EFEA] px-2 py-1 text-xs">
                    <img src={i.image_url} className="h-5 w-5 rounded-full object-cover" alt={i.name} />
                    <span>{i.name} × {i.quantity}</span>
                  </div>
                ))}
                {o.items.length > 5 && <span className="text-xs text-[#6B7B6C] px-2 py-1">+{o.items.length - 5} more</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Account;
